import OnboardingServices from '../../../../services/onboarding.services';
import { createAppAsyncThunk } from '../../../../common/thunk';
import { OnboardingRequest } from '../../onboardingRequest';
import { GeneralInfoBusinessFormState } from './Business/generalInfoBusinessSlice';
import { GeneralInfoMobilityFormState } from './Mobility/generalInfoMobilitySlice';
import { GeneralInfoSocialNetworkFormState } from './SocialNetworks/generalInfoSocialNetworksSlice';
import { GeneralInfoExternalLinksFormState } from './Links/generalInfoLinksSlice';
import { GeneralInfoAvailabilityFormState } from './Availability/generalInfoAvailablitySlice';
import {
  IBusinessInfo,
  IExternalLink,
  ILocation,
  IMobility,
  IOpeningTime,
  ISocialNetwork,
} from '../../../../common/interfaces/GeneralInfoInterfaces';
import { GeneralInfoLocationFormState } from './Locations/generalInfoLocationsSlice';
import { GeneralInfoState } from './generalInfoReducer';

export interface GeneralInfoPatchRequestData {
  shortName?: string;
  longName?: string;
  introduction?: string;
  businessInfo?: IBusinessInfo;
  location?: ILocation;
  locations?: ILocation[];
  mobility?: IMobility;
  language?: string | undefined;
  languages?: string[];
  openingTime?: IOpeningTime;
  socialNetwork?: ISocialNetwork[];
  externalLinks?: IExternalLink[];
}

export const prepareBusinessInfo = (business: GeneralInfoBusinessFormState): IBusinessInfo => {
  return {
    email: business.email,
    phone: business.phone,
    euVatNumber: business.vat,
    officialId: business.identifier,
    paymentMethod: business.paymentMethods,
  };
};

export const prepareAvailability = (availability: GeneralInfoAvailabilityFormState): IOpeningTime => {
  return {
    monday: {
      open: availability.monday.startTime,
      closed: availability.monday.endTime,
      availability: availability.monday.availability,
    },
    tuesday: {
      open: availability.tuesday.startTime,
      closed: availability.tuesday.endTime,
      availability: availability.tuesday.availability,
    },
    wednesday: {
      open: availability.wednesday.startTime,
      closed: availability.wednesday.endTime,
      availability: availability.wednesday.availability,
    },
    thursday: {
      open: availability.thursday.startTime,
      closed: availability.thursday.endTime,
      availability: availability.thursday.availability,
    },
    friday: {
      open: availability.friday.startTime,
      closed: availability.friday.endTime,
      availability: availability.friday.availability,
    },
    saturday: {
      open: availability.saturday.startTime,
      closed: availability.saturday.endTime,
      availability: availability.saturday.availability,
    },
    sunday: {
      open: availability.sunday.startTime,
      closed: availability.sunday.endTime,
      availability: availability.sunday.availability,
    },
  };
};

export const prepareMobility = (mobility: GeneralInfoMobilityFormState): IMobility | undefined => {
  if (!mobility.radius) return undefined;
  return {
    radius: +mobility.radius,
    unit: mobility.dimension ? 'miles' : 'km',
  };
};

export const prepareLocation = (locations: GeneralInfoLocationFormState[]): ILocation | undefined => {
  return (
    (locations.length && {
      name: locations[0].name,
      placeDescription: locations[0]?.address?.placeDescription,
      placeId: locations[0]?.address?.placeId,
      latitude: locations[0]?.address?.latitude || 0,
      longitude: locations[0]?.address?.longitude || 0,
    }) ||
    undefined
  );
};

export const prepareAdditionalLocations = (locations: GeneralInfoLocationFormState[]): ILocation[] => {
  return locations
    ?.filter((it, index) => index > 0)
    .map(it => ({
      name: it.name,
      placeDescription: it?.address?.placeDescription,
      placeId: it.address?.placeId,
      latitude: it.address?.latitude || 0,
      longitude: it.address?.longitude || 0,
    }));
};

export const prepareSocialNetworks = (networks: GeneralInfoSocialNetworkFormState[]): ISocialNetwork[] => {
  return networks?.map(it => ({ name: it.name || '', url: it.url }));
};

export const prepareExternalLinks = (links: GeneralInfoExternalLinksFormState[]): IExternalLink[] => {
  return links?.map(it => ({ linkDescription: it.description, url: it.url }));
};

export const prepareGeneralInfo = (generalInfo: GeneralInfoState): GeneralInfoPatchRequestData => {
  const { introduction, business, availability, mobility, locations, socialNetworks, externalLinks } = generalInfo;
  const businessInfoRequestData = prepareBusinessInfo(business);
  const availabilityRequestData = prepareAvailability(availability);
  const mobilityRequestData = prepareMobility(mobility);
  const locationRequestData = prepareLocation(locations);
  const locationsRequestData = prepareAdditionalLocations(locations);
  const socialNetworksRequestData = prepareSocialNetworks(socialNetworks);
  const externalLinksRequestData = prepareExternalLinks(externalLinks);
  return {
    shortName: introduction.logo,
    longName: introduction.header,
    introduction: introduction.description,
    businessInfo: businessInfoRequestData,
    language: business.mainLanguage,
    languages: business.additionalLanguages,
    mobility: mobilityRequestData,
    location: locationRequestData,
    locations: locationsRequestData,
    openingTime: availabilityRequestData,
    socialNetwork: socialNetworksRequestData,
    externalLinks: externalLinksRequestData,
  };
};

export const confirmGeneralInfoStep = createAppAsyncThunk('onboarding/confirmGeneralInfo', (requestData: OnboardingRequest) => {
  const id = requestData.id;
  if (!requestData.generalInfo) {
    throw Error('Missed General Info data');
  }
  return OnboardingServices.confirmOnboardingGeneralInfo(id, requestData.generalInfo).then(response => response.data);
});
